import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PermissionService } from '../../core/services/permission.service';
import { PosStateService } from './pos-state.service';

/**
 * P2 — Reusable POS route permission guard (shared; reused by P3+).
 *
 * Returns a CanActivateFn that allows the route only when the user holds
 * `code`, otherwise redirects to the POS terminal with a `?denied=<code>`
 * query param the shell can surface as a clean toast.
 *
 * FAIL-OPEN by design: PermissionService.hasPermission falls back to
 * role-based auth when the new-model permission set hasn't loaded yet, so a
 * legitimate Cashier is never locked out on transient bootstrap state —
 * while a Waiter (who lacks the role mapping for e.g. pos.payment.basic) is
 * still correctly denied. This is the single choke-point that closes ALL
 * paths to a gated screen (table tap, order-screen button, direct URL, any
 * future entry); the matching in-component check is defence-in-depth.
 *
 * Usage:
 *   canActivate: [posGuard, posAuthGuard, posPermissionGuard('pos.payment.basic')]
 */
export function posPermissionGuard(code: string): CanActivateFn {
  return () => {
    const perm = inject(PermissionService);
    const router = inject(Router);
    return perm.hasPermission(code)
      ? true
      : router.createUrlTree(['/pos/terminal'], { queryParams: { denied: code } });
  };
}

/**
 * Bug B — settle route guard keyed to the POS PIN identity.
 *
 * Uses PosStateService.canSettlePayment, which derives from the PIN'd-in
 * user's OWN roles (posRoles) — so a waiter-only terminal can NEVER reach
 * the settle screen, regardless of the PermissionService cache (which holds
 * the admin launcher's perms until Layer 2 makes it reflect the PIN user).
 *
 * Deliberately NOT posPermissionGuard('pos.payment.basic') for now: that
 * generic guard reads the leaky PermissionService. Once Layer 2 lands and
 * PermissionService reflects the PIN user, the settle route can move back to
 * the generic guard (and P3 can reuse it safely).
 */
export const posPaymentGuard: CanActivateFn = () => {
  const posState = inject(PosStateService);
  const router = inject(Router);
  return posState.canSettlePayment
    ? true
    : router.createUrlTree(['/pos/terminal'], { queryParams: { denied: 'pos.payment.basic' } });
};
